import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from "react";
import axios from "axios";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Bell, Inbox, ChevronDown, Package, ShoppingCart, Factory,
  ClipboardCheck, Truck, CheckCircle2, AlarmClock,
} from "lucide-react";
import { ClientsContext } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─────────────────────────────────────────────────────────────────────────────
   StageInboxBell — restructured per the user's spec.
   - Grouped, grid-row layout (28px 1fr 18px)
   - Hover-to-expand via CSS only (.det grid-template-rows transition)
   - Stage chips coloured by the real stage palette (--stage-*)
   - Client badge coloured by the per-client palette from ClientsContext
   - Overdue uses a calmer warning-ish tint, not high-saturation danger
   ──────────────────────────────────────────────────────────────────────────── */

/* Each stage carries its real palette (HSL triple from index.css :root vars)
   plus a lucide icon and the short label rendered in the chip. Mirrors
   /components/Layout.js nav so the inbox feels native to the app. */
const STAGE_META = {
  "PO Received":     { icon: Package,        hsl: "217 91% 60%", short: "PO Received"   },
  "Procurement":     { icon: ShoppingCart,   hsl: "25 95% 53%",  short: "Procurement"   },
  "Manufacturing":   { icon: Factory,        hsl: "271 91% 60%", short: "Manufacturing" },
  "Quality Control": { icon: ClipboardCheck, hsl: "172 66% 42%", short: "QC Check"      },
  "Dispatch":        { icon: Truck,          hsl: "38 92% 45%",  short: "Dispatch"      },
  "Completed":       { icon: CheckCircle2,   hsl: "215 14% 50%", short: "Completed"     },
};

/* Role → avatar color (mirrors ROLE_COLORS in AdminSettingsPanel.js).
   Values are HSL triples so we can compose with var(--alpha) at runtime
   while still honouring "no hex" inside the rendered DOM via inline style. */
const ROLE_HSL = {
  Admin:         "239 84% 67%",
  Received:      "217 91% 60%",
  Procurement:   "25 95% 53%",
  Manufacturing: "271 91% 65%",
  QC:            "172 66% 50%",
  Dispatch:      "38 92% 50%",
  Viewer:        "215 14% 50%",
  System:        "215 14% 50%",
};

function verbForTransition(targetStage, n) {
  const word = n === 1 ? "a line" : `${n} lines`;
  switch (targetStage) {
    case "Manufacturing":   return `moved ${word} to manufacturing`;
    case "Quality Control": return `moved ${word} to QC`;
    case "Dispatch":        return `sent ${word} to dispatch`;
    case "Completed":       return `dispatched ${word}`;
    default:                return `moved ${word} to ${targetStage}`;
  }
}

function verbForKind(kind, targetStage, n, extra = {}) {
  const word = n === 1 ? "a line" : `${n} lines`;
  if (kind === "blocked") return `blocked ${word} in ${targetStage}`;
  if (kind === "flagged") {
    if (extra.flag_kind === "expedite") return `flagged ${word} EXPEDITE in ${targetStage}`;
    if (extra.flag_kind === "parts_complete") return `received the last child part for ${word} in ${targetStage}`;
    return `set next action on ${word} in ${targetStage}`;
  }
  if (kind === "returned") return `returned ${word} to ${targetStage}`;
  return verbForTransition(targetStage, n);
}

function relativeBucket(iso) {
  if (!iso) return { label: "Just now", key: "now", date: null };
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = diffMs / 60000;
  if (mins <= 30) return { label: "Just now", key: "now", date: d };
  if (mins < 60)  return { label: "1 hour ago", key: "h1", date: d };
  const hrs = Math.floor(mins / 60);
  if (hrs >= 1 && hrs <= 12) {
    return { label: `${hrs} ${hrs === 1 ? "hour" : "hours"} ago`, key: `h${hrs}`, date: d };
  }
  if (hrs > 12 && hrs < 24) return { label: "Today, earlier", key: "today", date: d };
  const dayKey = d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  return { label: dayKey, key: `d${dayKey}`, date: d };
}

function relativeShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
}

function exactTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString())
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return `${String(d.getDate()).padStart(2, "0")}-${d.toLocaleString("en-US", { month: "short" })} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

function initials(name) {
  const s = (name || "?").trim();
  if (!s) return "?";
  const letters = s.split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join("");
  return (letters || s.slice(0, 2)).toUpperCase();
}

/* Group consecutive events (already sorted desc by created_at) that share:
   actor, prev_stage, target_stage, kind, and time-bucket. Overdue groups
   key on (kind, target_stage, bucket). */
function groupEvents(rows) {
  const groups = [];
  for (const r of rows) {
    const b = relativeBucket(r.created_at);
    const last = groups[groups.length - 1];
    const isOverdue = r.kind === "overdue";
    const key = isOverdue
      ? `ov|${r.target_stage}|${b.key}`
      : `${r.actor_name || ""}|${r.kind}|${r.prev_stage || ""}|${r.next_stage || ""}|${r.target_stage || ""}|${b.key}`;
    if (last && last.key === key) {
      last.items.push(r);
      last.totalQty += Number(r.item_qty || 0);
    } else {
      groups.push({
        key,
        kind: r.kind,
        actor_name: r.actor_name,
        actor_role: r.actor_role,
        prev_stage: r.prev_stage,
        next_stage: r.next_stage,
        target_stage: r.target_stage,
        bucket: b,
        items: [r],
        totalQty: Number(r.item_qty || 0),
        latest_at: r.created_at,
        is_overdue: isOverdue,
      });
    }
  }
  return groups;
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Render helpers                                                              */
/* ─────────────────────────────────────────────────────────────────────────── */

function StageChip({ stage, kind /* "from" | "to" */ }) {
  const meta = STAGE_META[stage];
  const Icon = meta?.icon || Package;
  const label = meta?.short || stage;
  const hsl = meta?.hsl || "215 14% 50%";
  // Both chips carry the stage's own palette. `to` is more saturated, `from`
  // is the same hue but at lower alpha so it visually recedes.
  const style = kind === "to"
    ? {
        background: `hsl(${hsl} / 0.16)`,
        color: `hsl(${hsl})`,
        borderColor: `hsl(${hsl} / 0.30)`,
      }
    : {
        background: `hsl(${hsl} / 0.07)`,
        color: `hsl(${hsl} / 0.85)`,
        borderColor: `hsl(${hsl} / 0.18)`,
      };
  return (
    <span className="sib-chip" style={style}>
      <Icon className="sib-chip-ic" strokeWidth={2} />
      <span>{label}</span>
    </span>
  );
}

function Avatar({ name, role, overdue }) {
  if (overdue) {
    return (
      <div className="sib-av sib-av--ov" aria-hidden>
        <AlarmClock size={11} strokeWidth={2.4} />
      </div>
    );
  }
  const hsl = ROLE_HSL[role] || ROLE_HSL.Viewer;
  const style = {
    background: `hsl(${hsl} / 0.16)`,
    color: `hsl(${hsl})`,
    borderColor: `hsl(${hsl} / 0.35)`,
  };
  return <div className="sib-av" style={style} aria-hidden>{initials(name)}</div>;
}

function hexToHsl(hex) {
  // Convert "#rrggbb" to "h s% l%" so we can compose with var-style alphas.
  if (!hex || typeof hex !== "string") return null;
  const m = hex.replace("#", "").match(/^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!m) return null;
  const r = parseInt(m[1], 16) / 255;
  const g = parseInt(m[2], 16) / 255;
  const b = parseInt(m[3], 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s; const l = (max + min) / 2;
  if (max === min) { h = 0; s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function PartCard({ row, clientHsl }) {
  const part = row.item_part_raw || row.item_part || "—";
  const po = row.item_po_number || "";
  const ln = row.item_line_number || "";
  const qty = Number(row.item_qty || 0);
  const customer = (row.item_client || "").trim();
  const desc = (row.item_description || row.item_part || part).trim();
  const meta = [
    qty > 0 && `${qty} pcs`,
    po && `PO ${po}`,
    ln && `LN ${ln}`,
    exactTime(row.created_at),
  ].filter(Boolean).join(" · ");

  const customerStyle = clientHsl
    ? { background: `hsl(${clientHsl} / 0.14)`, color: `hsl(${clientHsl})` }
    : undefined;

  return (
    <div className="sib-part">
      <div className="sib-part-top">
        {customer
          ? <span className="sib-part-customer" style={customerStyle}>{customer}</span>
          : <span className="sib-part-customer sib-part-customer--muted">—</span>}
        <span className="sib-part-pn">{part}</span>
      </div>
      <div className="sib-part-bottom">
        <span className="sib-part-desc">{desc}</span>
        <span className="sib-part-meta">{meta}</span>
      </div>
    </div>
  );
}

function GroupRow({ group, isUnread, onSeen, clientHslMap, expanded, onToggle, onDwellExpand }) {
  const rowRef = useRef(null);
  const seenRef = useRef(false);
  const dwellTimerRef = useRef(null);

  const markSeen = () => {
    if (!seenRef.current) {
      seenRef.current = true;
      onSeen?.(group);
    }
  };

  const handleEnter = () => {
    markSeen();
    // Dwell-to-expand: if the cursor stays on this row for ~1s, auto-expand.
    // Click is the explicit, immediate path.
    if (expanded) return;
    clearTimeout(dwellTimerRef.current);
    dwellTimerRef.current = setTimeout(() => {
      onDwellExpand?.(group.key);
    }, 1000);
  };
  const handleLeave = () => {
    clearTimeout(dwellTimerRef.current);
    dwellTimerRef.current = null;
  };
  const handleClick = () => {
    clearTimeout(dwellTimerRef.current);
    onToggle?.(group.key);
  };

  const n = group.items.length;
  const overdue = group.is_overdue;
  const fromStage = group.kind === "returned" ? group.next_stage : group.prev_stage;
  const toStage = group.target_stage;

  let verb;
  if (overdue) {
    verb = "";
  } else {
    verb = verbForKind(
      group.kind, toStage, n,
      { flag_kind: group.items[0]?.flag_kind }
    );
  }

  const showChips = !overdue && !!fromStage && !!toStage && (group.kind === "arrived" || group.kind === "returned");

  return (
    <div
      ref={rowRef}
      className={`sib-row${overdue ? " ov" : ""}${isUnread ? " unread" : ""}${expanded ? " is-open" : ""}`}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      data-testid={`stage-inbox-group-${group.key}`}
    >
      <Avatar name={group.actor_name} role={group.actor_role} overdue={overdue} />

      <div className="sib-c">
        {overdue ? (
          <div className="sib-sum">
            <span className="sib-sum-ov">{n} {n === 1 ? "line" : "lines"} overdue in</span>
            <StageChip stage={toStage} kind="to" />
            <span className="sib-dot">·</span>
            <span className="sib-time">{relativeShort(group.latest_at)}</span>
          </div>
        ) : (
          <div className="sib-sum">
            <span className="sib-actor">{group.actor_name || "Someone"}</span>
            <span className="sib-verb">{verb}</span>
            <span className="sib-dot">·</span>
            <span className="sib-time">{relativeShort(group.latest_at)}</span>
          </div>
        )}

        {showChips && (
          <div className="sib-chips">
            <StageChip stage={fromStage} kind="from" />
            <span className="sib-arrow" aria-hidden>→</span>
            <StageChip stage={toStage} kind="to" />
          </div>
        )}

        <div className="sib-det">
          <div className="sib-det-in" onClick={(e) => e.stopPropagation()}>
            {group.items.map((r, i) => (
              <PartCard
                key={r.id || i}
                row={r}
                clientHsl={clientHslMap?.[(r.item_client || "").trim()]}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="sib-chv">
        {n > 1 && <span className="sib-chv-n">{n}</span>}
        <ChevronDown size={13} strokeWidth={2.2} className="sib-chv-icon" />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Main component                                                              */
/* ─────────────────────────────────────────────────────────────────────────── */

export default function StageInboxBell() {
  const [open, setOpen] = useState(false);
  const [feed, setFeed] = useState({ unread: [], read: [], unread_count: 0 });
  const [hovered, setHovered] = useState(() => new Set());
  const firstPollRef = useRef(true);
  const clientsCtx = useContext(ClientsContext);
  const clients = clientsCtx?.clients || [];

  const listRef = useRef(null);

  /* Expanded row state — click-driven (toggles) with hover-dwell auto-expand.
     Once a row is expanded it stays open until the user clicks it again
     (or the popover closes). This removes the auto-collapse-on-leave that
     was causing layout shuffling between rows. */
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const toggleExpanded = useCallback((key) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const dwellExpand = useCallback((key) => {
    setExpandedKeys(prev => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  const clientHslMap = useMemo(() => {
    const m = {};
    for (const c of clients) {
      const hsl = hexToHsl(c.color);
      if (hsl) m[c.name] = hsl;
    }
    return m;
  }, [clients]);

  const fetchFeed = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/stage-inbox`);
      setFeed(res.data || { unread: [], read: [], unread_count: 0 });
    } catch { /* noop */ }
    firstPollRef.current = false;
  }, []);

  useEffect(() => {
    fetchFeed();
    const id = setInterval(fetchFeed, 60000);
    return () => clearInterval(id);
  }, [fetchFeed]);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/auth/stage-inbox/mark-read`);
      setFeed(prev => ({
        unread: [],
        read: [...(prev.unread || []), ...(prev.read || [])].slice(0, 30),
        unread_count: 0,
      }));
    } catch { /* noop */ }
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) {
      // Reset expanded rows so reopening starts fresh.
      setExpandedKeys(new Set());
      if ((feed.unread_count || 0) > 0) markAllRead();
    }
  };

  const count = feed.unread_count || 0;

  const unreadIds = useMemo(() => new Set((feed.unread || []).map(r => r.id)), [feed.unread]);
  const filteredGroups = useMemo(() => {
    const all = [...(feed.unread || []), ...(feed.read || [])];
    return groupEvents(all);
  }, [feed]);

  const handleGroupSeen = (g) => {
    setHovered(prev => {
      const next = new Set(prev);
      g.items.forEach(it => it.id && next.add(it.id));
      return next;
    });
  };

  /* Build buckets with dividers */
  const renderedRows = [];
  let lastBucketKey = null;
  for (const g of filteredGroups) {
    if (g.bucket.key !== lastBucketKey) {
      renderedRows.push({ type: "divider", label: g.bucket.label, key: `div-${g.bucket.key}-${renderedRows.length}` });
      lastBucketKey = g.bucket.key;
    }
    const isUnread = g.items.some(it => unreadIds.has(it.id) && !hovered.has(it.id));
    renderedRows.push({ type: "row", group: g, isUnread, key: g.key });
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`relative inline-flex items-center justify-center w-8 h-8 rounded-full transition-[transform,colors,box-shadow] duration-150 ${
            open
              ? "bg-zinc-200 dark:bg-zinc-800 text-[hsl(var(--muted-foreground))] [box-shadow:inset_0_1px_3px_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.06)]"
              : count > 0
                ? "text-orange-600 rl-frost-btn"
                : "text-[hsl(var(--muted-foreground))] rl-frost-btn hover:text-[hsl(var(--foreground))]"
          }`}
          title={count > 0 ? `${count} new stage update${count === 1 ? "" : "s"}` : "Stage inbox"}
          data-testid="stage-inbox-trigger"
        >
          <Bell className="w-3.5 h-3.5" strokeWidth={2.2} />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold leading-none shadow-sm">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="z-50 p-0 w-[440px] max-h-[78vh] overflow-hidden rounded-xl border rl-frost flex flex-col"
        data-testid="stage-inbox-panel"
      >
        <SibStyles />

        <div className="sib-root flex flex-col h-full max-h-[78vh] min-h-0">
          {/* Header */}
          <div className="sib-header">
            <div className="sib-header-l">
              <span className="sib-header-title">MY STAGE MOVEMENTS</span>
              {count > 0 && <span className="sib-header-pill">{count} new</span>}
            </div>
            <Bell size={15} className="sib-header-bell" />
          </div>

          {/* List */}
          <div className="sib-list" ref={listRef}>
            {renderedRows.length === 0 ? (
              <div className="sib-empty">
                <Inbox className="w-8 h-8 opacity-30 mb-2" strokeWidth={1.2} />
                <p className="text-sm">All caught up</p>
                <p className="text-[11px] mt-0.5">You'll see arrivals, blocks, and overdues here.</p>
              </div>
            ) : (
              renderedRows.map(r => {
                if (r.type === "divider") {
                  return <div key={r.key} className="sib-divider">{r.label}</div>;
                }
                return (
                  <GroupRow
                    key={r.key}
                    group={r.group}
                    isUnread={r.isUnread}
                    onSeen={handleGroupSeen}
                    expanded={expandedKeys.has(r.key)}
                    onToggle={toggleExpanded}
                    onDwellExpand={dwellExpand}
                    clientHslMap={clientHslMap}
                  />
                );
              })
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Scoped styles — alias spec tokens onto the app's HSL design vars            */
/* ─────────────────────────────────────────────────────────────────────────── */

function SibStyles() {
  return (
    <style>{`
      .sib-root {
        /* Color tokens (aliased onto app HSL vars; no hex) */
        --color-text-primary:    hsl(var(--foreground));
        --color-text-secondary:  hsl(var(--foreground) / 0.78);
        --color-text-tertiary:   hsl(var(--foreground) / 0.55);
        --color-text-info:       hsl(var(--primary));
        --color-text-danger:     hsl(var(--destructive));
        --color-text-warning:    hsl(38 92% 38%);

        --color-background-primary:   hsl(var(--popover));
        --color-background-secondary: hsl(var(--muted));
        --color-background-tertiary:  hsl(var(--muted) / 0.55);
        --color-background-info:      hsl(var(--primary) / 0.14);
        --color-background-danger:    hsl(var(--destructive) / 0.10);
        --color-background-warning:   hsl(38 92% 45% / 0.10);

        --color-border-tertiary:      hsl(var(--border) / 0.7);
        --color-border-secondary:     hsl(var(--border));

        color: var(--color-text-primary);
        background: var(--color-background-primary);
        font-family: 'Onest', sans-serif;
      }

      /* ── Header ──────────────────────────────────────────────────────── */
      .sib-header {
        display: flex; align-items: center; justify-content: space-between;
        padding: 8px 13px;
        border-bottom: 0.5px solid var(--color-border-tertiary);
        background: var(--color-background-tertiary);
      }
      .sib-header-l { display: flex; align-items: center; gap: 8px; }
      .sib-header-title {
        font-size: 11px; font-weight: 600; letter-spacing: 0.07em;
        text-transform: uppercase; color: var(--color-text-secondary);
      }
      .sib-header-pill {
        font-size: 10px; font-weight: 500;
        background: var(--color-background-info); color: var(--color-text-info);
        padding: 1px 7px; border-radius: 99px;
      }
      .sib-header-bell { color: var(--color-text-secondary); }

      /* ── List ────────────────────────────────────────────────────────── */
      .sib-list {
        flex: 1; min-height: 0; overflow-y: auto;
        background: var(--color-background-primary);
        /* Native scroll-anchoring: keep the currently-hovered row stable
           when content above changes size. */
        overflow-anchor: auto;
      }
      .sib-row { overflow-anchor: none; }
      .sib-row:hover { overflow-anchor: auto; }
      .sib-empty {
        display: flex; flex-direction: column; align-items: center;
        justify-content: center; padding: 40px 24px; text-align: center;
        color: var(--color-text-secondary);
      }
      .sib-divider {
        font-size: 10px; font-weight: 500; letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 4px 13px;
        background: var(--color-background-tertiary);
        color: var(--color-text-tertiary);
        border-bottom: 0.5px solid var(--color-border-tertiary);
      }

      /* ── Row ─────────────────────────────────────────────────────────── */
      .sib-row {
        display: grid;
        grid-template-columns: 28px 1fr 18px;
        gap: 0 8px;
        padding: 8px 13px;
        border-bottom: 0.5px solid var(--color-border-tertiary);
        cursor: pointer;
        outline: none;
        transition: background-color .18s ease;
      }
      /* Overdue rows: smaller avatar, tighter padding, smaller text — these
         are recurring noise so they shouldn't dominate the stack. */
      .sib-row.ov {
        grid-template-columns: 20px 1fr 18px;
        padding: 4px 13px;
        gap: 0 8px;
      }
      .sib-row:hover { background: var(--color-background-tertiary); }
      .sib-row:focus-visible { background: var(--color-background-tertiary); }
      /* Expanded rows carry a soft warm off-white surface so they read
         as "open" without the heavy grey wash. Dark mode gets a subtle
         elevated dark surface instead so the contrast doesn't invert. */
      .sib-row.is-open {
        background: hsl(40 30% 97%);
      }
      .sib-row.is-open:hover {
        background: hsl(40 30% 95%);
      }
      :root.dark .sib-row.is-open {
        background: hsl(220 14% 14%);
      }
      :root.dark .sib-row.is-open:hover {
        background: hsl(220 14% 16%);
      }
      .sib-row.unread { box-shadow: inset 2.5px 0 0 var(--color-text-info); }
      .sib-row.ov.unread { box-shadow: inset 2.5px 0 0 var(--color-text-warning); }

      .sib-av {
        width: 28px; height: 28px; border-radius: 99px;
        background: var(--color-background-secondary);
        color: var(--color-text-primary);
        border: 0.5px solid var(--color-border-secondary);
        display: inline-flex; align-items: center; justify-content: center;
        font-size: 10px; font-weight: 600; letter-spacing: 0.02em;
      }
      .sib-av--ov {
        width: 20px; height: 20px;
        background: var(--color-background-warning);
        color: var(--color-text-warning);
        border-color: transparent;
      }

      .sib-c { min-width: 0; display: flex; flex-direction: column; gap: 4px; }

      .sib-sum {
        display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px;
        min-width: 0;
      }
      .sib-actor { font-size: 13px; font-weight: 700; color: var(--color-text-primary); }
      .sib-verb  { font-size: 12.5px; font-weight: 500; color: var(--color-text-secondary); }
      .sib-dot   { font-size: 11px; color: var(--color-text-tertiary); }
      .sib-time  { font-size: 11px; color: var(--color-text-tertiary); }
      .sib-sum-ov {
        font-size: 12px; font-weight: 600; color: var(--color-text-warning);
      }
      /* Tighten overdue row's content spacing */
      .sib-row.ov .sib-c { gap: 2px; }
      .sib-row.ov .sib-sum { font-size: 12px; }
      .sib-row.ov .sib-time,
      .sib-row.ov .sib-dot { font-size: 10px; }
      .sib-row.ov .sib-chv-icon { width: 11px; height: 11px; }

      /* Stage chips */
      .sib-chips { display: flex; align-items: center; gap: 6px; min-width: 0; }
      .sib-chip {
        display: inline-flex; align-items: center; gap: 4px;
        font-size: 11px; font-weight: 500;
        padding: 1px 7px 1px 5px;
        border-radius: 5px;
        border: 0.5px solid transparent;
        max-width: 100%;
      }
      .sib-chip span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .sib-chip-ic { width: 11px; height: 11px; flex-shrink: 0; }
      .sib-arrow { font-size: 11px; color: var(--color-text-tertiary); }

      /* ── Expand: state-driven (.is-open) instead of :hover ─────────────── */
      .sib-det {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows .22s ease;
      }
      .sib-row.is-open .sib-det {
        grid-template-rows: 1fr;
      }
      .sib-det-in {
        overflow: hidden;
        min-height: 0;
      }
      /* Recessed/pressed surface applies only while the row is open so the
         decoration doesn't leak above a collapsed row. */
      .sib-row.is-open .sib-det-in {
        margin-top: 6px;
        padding: 4px 10px 8px 10px;
        border-radius: 6px;
        background: hsl(var(--foreground) / 0.05);
        box-shadow:
          inset 0 1px 2px hsl(var(--foreground) / 0.08),
          inset 0 0 0 0.5px hsl(var(--foreground) / 0.04);
      }

      /* Chevron column */
      .sib-chv {
        display: flex; flex-direction: column; align-items: center;
        padding-top: 4px; gap: 1px;
        color: var(--color-text-tertiary);
      }
      .sib-chv-icon { opacity: 0.5; transition: opacity .18s, transform .22s cubic-bezier(.4, 1.4, .6, 1); }
      .sib-row:hover .sib-chv-icon,
      .sib-row:focus-visible .sib-chv-icon {
        opacity: 1;
      }
      .sib-row.is-open .sib-chv-icon {
        opacity: 1;
        transform: rotate(180deg);
      }
      .sib-chv-n {
        font-size: 9px; font-weight: 500; opacity: 0.7;
        color: var(--color-text-tertiary);
        line-height: 1;
      }
      .sib-row:hover .sib-chv-n,
      .sib-row:focus-visible .sib-chv-n,
      .sib-row.is-open .sib-chv-n { opacity: 1; }

      /* ── Part detail cards inside .det-in ────────────────────────────── */
      .sib-part {
        padding: 6px 0;
        display: flex; flex-direction: column; gap: 2px;
        border-top: 0.5px solid hsl(var(--foreground) / 0.08);
      }
      .sib-part:first-child { border-top: none; padding-top: 4px; }
      .sib-part-top {
        display: flex; align-items: center; justify-content: space-between;
        gap: 8px; min-width: 0;
      }
      .sib-part-customer {
        font-size: 11px; font-weight: 500;
        background: var(--color-background-info);
        color: var(--color-text-info);
        padding: 1px 8px; border-radius: 99px;
        white-space: nowrap;
        max-width: 60%; overflow: hidden; text-overflow: ellipsis;
      }
      .sib-part-customer--muted {
        background: var(--color-background-secondary);
        color: var(--color-text-tertiary);
      }
      .sib-part-pn {
        font-family: var(--rl-font-data), sans-serif;
        font-variant-numeric: tabular-nums;
        font-size: 11px;
        background: var(--color-background-secondary);
        border: 0.5px solid var(--color-border-secondary);
        border-radius: 4px;
        padding: 1px 6px;
        max-width: 50%;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        color: var(--color-text-primary);
        /* Part-# is the only field operators copy out — text cursor + selectable. */
        cursor: text;
        user-select: all;
      }
      .sib-part-bottom {
        display: flex; align-items: baseline; justify-content: space-between;
        gap: 8px; min-width: 0;
      }
      .sib-part-desc {
        font-size: 12px; font-weight: 500;
        color: var(--color-text-primary);
        min-width: 0; flex: 1 1 auto;
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      }
      .sib-part-meta {
        font-size: 10px; color: var(--color-text-tertiary);
        white-space: nowrap; flex-shrink: 0;
      }
    `}</style>
  );
}
